import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarDays, Users, FileCheck, DollarSign } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import {
  StatCard,
  PendingCommissionsCard,
  ExpenseAlertsCard,
  IGSSStatsCard,
  TodayAppointmentsList,
  RecentPatientsList,
  API_URL,
  formatCurrency
} from '../components/dashboard';

const Dashboard = () => {
  const { token, hasRole } = useAuth();
  const [stats, setStats] = useState({
    citas_hoy: 0,
    pacientes_registrados: 0,
    examenes_mes: 0,
    ingresos_mes: 0,
  });
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [recentPatients, setRecentPatients] = useState([]);
  const [pendingCommissions, setPendingCommissions] = useState(0);
  const [igssStats, setIgssStats] = useState({ count: 0, total: 0 });
  const [expenseAlerts, setExpenseAlerts] = useState(null);
  const [monthExpenses, setMonthExpenses] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, appointmentsRes, patientsRes, igssRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`),
        axios.get(`${API_URL}/api/dashboard/today-appointments`),
        axios.get(`${API_URL}/api/dashboard/recent-patients`),
        axios.get(`${API_URL}/api/dashboard/igss-stats`),
      ]);
      
      setStats(statsRes.data);
      setTodayAppointments(appointmentsRes.data);
      setRecentPatients(patientsRes.data);
      setIgssStats(igssRes.data);
      
      // Fetch pending commissions for admin/superadmin
      if (hasRole(['ADMIN', 'SUPERADMIN'])) {
        try {
          const [commRes, expenseAlertsRes] = await Promise.all([
            axios.get(`${API_URL}/api/commissions/pending-total`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${API_URL}/api/expenses/dashboard-alerts`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          ]);
          setPendingCommissions(commRes.data.total_pending);
          setExpenseAlerts(expenseAlertsRes.data);
          
          // Get current month expenses
          const today = new Date();
          const monthRes = await axios.get(
            `${API_URL}/api/expenses/month-total/${today.getFullYear()}/${today.getMonth() + 1}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setMonthExpenses(monthRes.data.total);
        } catch (e) {
          console.error('Error fetching admin data:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Citas de Hoy',
      value: stats.citas_hoy,
      icon: CalendarDays,
      borderColor: 'border-l-brand-main',
    },
    {
      title: 'Pacientes Registrados',
      value: stats.pacientes_registrados,
      icon: Users,
      borderColor: 'border-l-brand-cyan',
    },
    {
      title: 'Exámenes del Mes',
      value: stats.examenes_mes,
      icon: FileCheck,
      borderColor: 'border-l-emerald-500',
    },
    {
      title: 'Ingresos del Mes',
      value: formatCurrency(stats.ingresos_mes),
      icon: DollarSign,
      borderColor: 'border-l-amber-500',
      isAmount: true,
      subtitle: hasRole(['ADMIN', 'SUPERADMIN']) && monthExpenses > 0 
        ? `Gastos del mes: ${formatCurrency(monthExpenses)}` 
        : null,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse bg-white border border-slate-200">
              <CardContent className="p-6">
                <div className="h-20 bg-slate-100 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <StatCard
            key={stat.title}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            borderColor={stat.borderColor}
            isAmount={stat.isAmount}
            subtitle={stat.subtitle}
            testId={`stat-card-${index}`}
          />
        ))}
      </div>

      {/* Pending Commissions Card - Only for ADMIN/SUPERADMIN */}
      {hasRole(['ADMIN', 'SUPERADMIN']) && (
        <PendingCommissionsCard pendingCommissions={pendingCommissions} />
      )}

      {/* Expense Alerts Card - Only for ADMIN/SUPERADMIN */}
      {hasRole(['ADMIN', 'SUPERADMIN']) && (
        <ExpenseAlertsCard expenseAlerts={expenseAlerts} />
      )}

      {/* IGSS Stats Card */}
      <IGSSStatsCard igssStats={igssStats} />

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TodayAppointmentsList appointments={todayAppointments} />
        <RecentPatientsList patients={recentPatients} />
      </div>
    </div>
  );
};

export default Dashboard;
